import { API_CONFIG, apiCall, authFetch, getHeadersWithOrg } from './client';
import { PLATFORM_ENDPOINTS } from '../../config/config';
import { ErrorResponse } from '../../types';
import { TemplateConfig } from '../../features/template-designer/model/types';
import { validateTemplate } from '../../features/template-designer/io/importTemplate';

/**
 * Error thrown by {@link saveTemplateChecked} that carries the HTTP status code
 * so the caller can distinguish a 403 (missing org_admin role for custom JS)
 * and a 422 (custom CSS/JS size limit exceeded) from other failures. Mirrors the
 * `PublishError` pattern — `apiCall` collapses every non-OK status into a plain
 * `Error` with no status to branch on, so the custom-code save path uses
 * `authFetch` directly and throws this instead.
 */
export class TemplateSaveError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'TemplateSaveError';
    this.status = status;
  }
}

/**
 * Save (upsert) the Template Designer config for a site.
 *
 * The backend stores one template per site and mirrors the designer's wire JSON
 * 1:1, so the request body is the already-serialized config string verbatim
 * (`serializeTemplate(config)` from the designer's `io/exportTemplate`). It is
 * NOT re-stringified or remapped here — the camelCase keys are exactly what the
 * backend model expects. The endpoint requires the `site:update:site`
 * permission, so it goes through the authenticated API client.
 *
 * Returns the stored config as the backend echoes it back (same wire shape).
 */
export async function saveTemplate<T = unknown>(
  siteId: string,
  serializedConfig: string,
): Promise<T> {
  return apiCall<T>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.SITE_TEMPLATE(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: serializedConfig,
    },
  );
}

/**
 * Save the template like {@link saveTemplate}, but surface the HTTP status on
 * failure via {@link TemplateSaveError} so the caller can show a specific
 * message for 403 (custom JS needs org_admin) and 422 (CSS/JS size limit).
 *
 * Uses `authFetch` directly (not `apiCall`) for the same reason `getTemplate`
 * does — `apiCall` discards the status, which the custom-code tabs must branch
 * on. The wire body is the already-serialized config string (camelCase keys the
 * backend mirrors 1:1), identical to `saveTemplate`.
 */
export async function saveTemplateChecked(
  siteId: string,
  serializedConfig: string,
): Promise<void> {
  const response = await authFetch(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.SITE_TEMPLATE(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: serializedConfig,
    },
  );
  if (!response.ok) {
    const errorData: ErrorResponse = await response
      .json()
      .catch(() => ({ error: `HTTP ${response.status}` }));
    const message =
      errorData.detail || errorData.error || `Save failed: ${response.status}`;
    throw new TemplateSaveError(response.status, message);
  }
}

/**
 * Load the saved Template Designer config for a site.
 *
 * The backend stores one template per site and echoes back the designer's wire
 * JSON 1:1, so the response is validated through the same `validateTemplate`
 * shape check used for file import — a stale/malformed stored template yields
 * `null` (the editor then keeps its default preset) rather than corrupting the
 * reducer. A 404 means the site has no saved template yet and also returns
 * `null`; any other non-OK status throws.
 *
 * Uses `authFetch` directly (not `apiCall`) because we must distinguish a 404
 * from other failures, and `apiCall` collapses every non-OK status into a
 * generic `Error` with no status to branch on.
 */
export async function getTemplate(siteId: string): Promise<TemplateConfig | null> {
  const response = await authFetch(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.SITE_TEMPLATE(siteId)}`,
    { headers: getHeadersWithOrg() },
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to load template: ${response.status}`);
  }
  const parsed = await response.json();
  const result = validateTemplate(parsed);
  return result.ok ? result.config : null;
}
